import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import ClassOverModal from '../../components/ClassOverModal';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import { useOnStudentChange, useStudentCard } from '../../context/StudentCardContext';
import {
  addStudent, addStudentForce,
  addSalesperson,
  getAdminMentors,
  getSalespeople,
  getStudents,
  markStudentLeave,
  raiseClassOver,
  reactivateStudent,
  reassignStudent,
  removeStudent,
  updateStudentEmail,
  updateStudentPhone,
} from '../../lib/api';
import type { MentorInfo, SalesPersonInfo, StudentFull } from '../../lib/api';

type ModalMode = 'add' | 'reassign' | 'reactivate' | 'leave' | 'email' | 'phone' | 'deactivate' | null;

const ManageStudents: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const { onStudentEnter, onStudentMove, onStudentLeave } = useStudentCard();
  const [students, setStudents] = useState<StudentFull[]>([]);
  const [mentors, setMentors] = useState<MentorInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<ModalMode>(null);
  const [selected, setSelected] = useState<StudentFull | null>(null);

  // Add-student form state
  const [salespeople, setSalespeople] = useState<SalesPersonInfo[]>([]);
  const [newName, setNewName] = useState('');
  const [newCourse, setNewCourse] = useState('');
  const [newMentorId, setNewMentorId] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newSessionType, setNewSessionType] = useState<'group' | 'one-to-one'>('group');
  const [newSalesPerson, setNewSalesPerson] = useState('');
  const [newRegPaid, setNewRegPaid] = useState('1000');
  const [newCourseFee, setNewCourseFee] = useState('20000');
  const [sendWelcome, setSendWelcome] = useState(false);

  // Institute leave state
  const [leaveDate, setLeaveDate] = useState(new Date().toISOString().slice(0, 10));
  const [leaveReason, setLeaveReason] = useState('');
  const [leaveMentorId, setLeaveMentorId] = useState('');

  // Edit email / phone state
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');

  // Reassign state
  const [reassignMentorId, setReassignMentorId] = useState('');

  // Reactivate state
  const [reactivateMentorId, setReactivateMentorId] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'active' | 'inactive'>('active');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, mRes, spRes] = await Promise.all([
        getStudents(adminPassword),
        getAdminMentors(adminPassword),
        getSalespeople(adminPassword),
      ]);
      setStudents(sRes.students);
      setMentors(mRes.mentors.filter(m => m.status === 'Active'));
      setSalespeople(spRes.salespeople);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [adminPassword]);

  useEffect(() => { load(); }, [load]);
  useOnStudentChange(load);   // deactivated / reassigned from the student popup

  const mentorName = (id: string) => mentors.find(m => m.mentorId === id)?.mentorName ?? id;

  const activeStudents   = students.filter(s => s.status === 'Active');
  const inactiveStudents = students.filter(s => s.status !== 'Active');

  const visible = (activeTab === 'active' ? activeStudents : inactiveStudents).filter(s =>
    !search ||
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.studentId.toLowerCase().includes(search.toLowerCase()) ||
    (s.course || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleAdd = async (e: React.FormEvent, force = false) => {
    e.preventDefault();
    if (!newName || !newCourse || !newMentorId) { toast.error('Fill all required fields'); return; }
    setSubmitting(true);
    try {
      const fn = force ? addStudentForce : addStudent;
      const res = await fn(adminPassword, newName, newCourse, newMentorId, newNotes, newEmail, sendWelcome, newSessionType, newSalesPerson, Number(newRegPaid) || 1000, Number(newCourseFee) || 0);
      if (!res.ok && (res as any).duplicate) {
        const d = res as any;
        if (window.confirm(`⚠️ Duplicate detected!\n\nExisting student: ${d.existingName} (${d.existingId})\n\nAdd anyway as a new student?`)) {
          await handleAdd(e, true);
        }
        return;
      }
      toast.success(`Student added: ${(res as any).studentId}`);
      setModal(null);
      setNewName(''); setNewCourse(''); setNewMentorId(''); setNewNotes(''); setNewEmail(''); setSendWelcome(false); setNewSessionType('group'); setNewSalesPerson(''); setNewRegPaid('1000'); setNewCourseFee('20000');
      load();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLeave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leaveDate || !leaveReason.trim()) { toast.error('Date and reason are required'); return; }
    setSubmitting(true);
    try {
      const res = await markStudentLeave(adminPassword, leaveDate, leaveReason, leaveMentorId || undefined);
      toast.success(`Leave marked for ${res.count} student(s). Emails sent where available.`);
      setModal(null);
      setLeaveReason(''); setLeaveMentorId('');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = (s: StudentFull) => {
    setSelected(s);
    setModal('deactivate');
  };

  // Student Coordinators finishing a course raise Class Over, so admin is
  // emailed the class report and notified (admin's own deactivation is as before).
  const coordinatorId = adminPassword.startsWith('COORD:') ? adminPassword.slice(6) : '';
  const [classOverStudent, setClassOverStudent] = useState<StudentFull | null>(null);

  const handleDeactivateConfirm = async (reason: 'ranAway' | 'courseFinished') => {
    if (!selected) return;
    setSubmitting(true);
    try {
      if (reason === 'courseFinished' && coordinatorId) {
        await raiseClassOver(coordinatorId, selected.studentId, '');
      } else {
        await removeStudent(adminPassword, selected.studentId, reason);
      }
      toast.success(
        reason === 'ranAway'
          ? `${selected.name} marked as Ran Away and deactivated`
          : coordinatorId
            ? `Class Over raised for ${selected.name} — admin has been notified`
            : `${selected.name} deactivated (Course Finished)`
      );
      setModal(null);
      setSelected(null);
      load();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReactivate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected || !reactivateMentorId) return;
    setSubmitting(true);
    try {
      await reactivateStudent(adminPassword, selected.studentId, reactivateMentorId);
      toast.success(`${selected.name} reactivated and assigned to ${mentorName(reactivateMentorId)}`);
      setModal(null);
      setSelected(null);
      setReactivateMentorId('');
      load();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmailSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    try {
      await updateStudentEmail(adminPassword, selected.studentId, editEmail);
      toast.success(`Email updated for ${selected.name}`);
      setModal(null);
      setSelected(null);
      load();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhoneSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    try {
      await updateStudentPhone(adminPassword, selected.studentId, editPhone);
      toast.success(`Phone updated for ${selected.name}`);
      setModal(null);
      setSelected(null);
      load();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReassign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected || !reassignMentorId) return;
    setSubmitting(true);
    try {
      await reassignStudent(adminPassword, selected.studentId, reassignMentorId);
      toast.success(`${selected.name} reassigned to ${mentorName(reassignMentorId)}`);
      setModal(null);
      setSelected(null);
      setReassignMentorId('');
      load();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <TrackerLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-black" style={{ color: '#312741' }}>Students</h1>
            <p className="text-xs text-gray-400 mt-0.5">
              {activeStudents.length} active · {inactiveStudents.length} deactivated
            </p>
          </div>
          {activeTab === 'active' && (
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setModal('leave')}
                className="px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors border-2 border-red-200 text-red-600 bg-red-50 hover:bg-red-100"
              >
                Mark Leave / Holiday
              </button>
              <button
                onClick={() => setModal('add')}
                className="px-5 py-2.5 rounded-xl font-bold text-sm shadow-sm transition-all hover:opacity-90 active:scale-95"
                style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)', color: '#2b2240' }}
              >
                + Add Student
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-xl bg-gray-100 w-fit">
          <button
            onClick={() => setActiveTab('active')}
            className="px-5 py-2 rounded-lg text-sm font-bold transition-all"
            style={activeTab === 'active'
              ? { background: '#2b2240', color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.15)' }
              : { background: 'transparent', color: '#6b7280' }}>
            Active
            <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full"
              style={{ background: activeTab === 'active' ? 'rgba(255,255,255,0.2)' : '#e5e7eb',
                       color:      activeTab === 'active' ? '#fff' : '#6b7280' }}>
              {activeStudents.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('inactive')}
            className="px-5 py-2 rounded-lg text-sm font-bold transition-all"
            style={activeTab === 'inactive'
              ? { background: '#78716c', color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.15)' }
              : { background: 'transparent', color: '#6b7280' }}>
            Deactivated
            {inactiveStudents.length > 0 && (
              <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full"
                style={{ background: activeTab === 'inactive' ? 'rgba(255,255,255,0.25)' : '#fde68a',
                         color:      activeTab === 'inactive' ? '#fff' : '#92400e' }}>
                {inactiveStudents.length}
              </span>
            )}
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <input
            type="text"
            placeholder={activeTab === 'active' ? 'Search active students by name, ID or course…' : 'Search deactivated students…'}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400 transition-colors bg-white"
          />
        </div>

        {/* Inactive info banner */}
        {activeTab === 'inactive' && inactiveStudents.length > 0 && (
          <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl text-sm bg-amber-50 border border-amber-200 text-amber-800">
            <span className="text-base flex-shrink-0">⚠️</span>
            <p>Deactivated students are shown for reference. Only <strong>Reactivate</strong> is available — all other actions are disabled.</p>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex flex-col items-center justify-center h-40 gap-3 text-gray-400">
            <div className="w-6 h-6 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Loading students…</span>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: activeTab === 'active'
                    ? 'linear-gradient(135deg,#1e1730,#2b2240)'
                    : '#78716c' }}>
                    <Th>ID</Th>
                    <Th>Name</Th>
                    <Th>Mentor</Th>
                    <Th>Course</Th>
                    <Th>Status</Th>
                    <Th>Joined</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-14 text-gray-400">
                        <div className="text-4xl mb-2">{activeTab === 'active' ? '👥' : '✅'}</div>
                        {activeTab === 'active' ? 'No active students found' : 'No deactivated students'}
                      </td>
                    </tr>
                  ) : visible.map(s => {
                    const isInactive = s.status !== 'Active';
                    const statusColor = s.status === 'Active' ? { bg: '#dcfce7', text: '#15803d' }
                      : s.status === 'Ran Away'        ? { bg: '#fee2e2', text: '#b91c1c' }
                      : s.status === 'Institute Leave' ? { bg: '#fef9c3', text: '#92400e' }
                      : { bg: '#f3f4f6', text: '#6b7280' };
                    return (
                      <tr key={s.studentId}
                        className="border-b border-gray-100 transition-colors"
                        style={{ background: isInactive ? '#fafaf9' : undefined,
                                 opacity: isInactive ? 0.75 : 1 }}
                        onMouseEnter={e => { if (!isInactive) (e.currentTarget as HTMLElement).style.background = '#fefce8'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = isInactive ? '#fafaf9' : ''; }}>
                        <Td>
                          <span className="font-mono font-bold text-xs" style={{ color: isInactive ? '#9ca3af' : '#312741' }}>
                            {s.studentId}
                          </span>
                        </Td>
                        <Td>
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-black text-white flex-shrink-0"
                              style={{ background: isInactive ? '#9ca3af' : '#2b2240' }}>
                              {s.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                            </div>
                            <span
                              className={`font-semibold cursor-default ${isInactive ? 'text-gray-400 line-through' : 'text-gray-800'}`}
                              onMouseEnter={e => !isInactive && onStudentEnter(s.studentId, s.name, e)}
                              onMouseMove={onStudentMove}
                              onMouseLeave={onStudentLeave}
                            >
                              {s.name}
                            </span>
                          </div>
                        </Td>
                        <Td><span className={isInactive ? 'text-gray-400' : 'text-gray-600'}>{mentorName(s.mentorId)}</span></Td>
                        <Td><span className="text-xs" style={{ color: isInactive ? '#9ca3af' : '#6b7280' }}>{s.course}</span></Td>
                        <Td>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold"
                            style={{ background: statusColor.bg, color: statusColor.text }}>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusColor.text }} />
                            {s.status}
                          </span>
                        </Td>
                        <Td><span className="text-xs text-gray-400">{s.dateJoined}</span></Td>
                        <Td>
                          {isInactive ? (
                            /* Inactive: only reactivate */
                            <button
                              onClick={() => { setSelected(s); setReactivateMentorId(s.mentorId); setModal('reactivate'); }}
                              className="px-3 py-1.5 rounded-lg text-xs font-bold border-2 border-green-300 text-green-700 bg-green-50 hover:bg-green-100 transition-colors"
                            >
                              ↑ Reactivate
                            </button>
                          ) : (
                            /* Active: full actions */
                            <div className="flex gap-1.5 flex-wrap">
                              <button
                                onClick={() => { setSelected(s); setReassignMentorId(s.mentorId); setModal('reassign'); }}
                                className="px-2.5 py-1 rounded-lg text-xs font-medium border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors"
                              >
                                Reassign
                              </button>
                              <button
                                onClick={() => { setSelected(s); setEditEmail(s.email || ''); setModal('email'); }}
                                className="px-2.5 py-1 rounded-lg text-xs font-medium border border-purple-200 text-purple-600 hover:bg-purple-50 transition-colors"
                                title={s.email ? `Email: ${s.email}` : 'No email set'}
                              >
                                {s.email ? 'Edit Email' : 'Add Email'}
                              </button>
                              <button
                                onClick={() => { setSelected(s); setEditPhone((s as any).phone || ''); setModal('phone'); }}
                                className="px-2.5 py-1 rounded-lg text-xs font-medium border border-teal-200 text-teal-600 hover:bg-teal-50 transition-colors"
                                title={(s as any).phone ? `Phone: ${(s as any).phone}` : 'No phone set'}
                              >
                                {(s as any).phone ? 'Edit Phone' : 'Add Phone'}
                              </button>
                              {coordinatorId && (
                                <button
                                  onClick={() => setClassOverStudent(s)}
                                  className="px-2.5 py-1 rounded-lg text-xs font-bold border border-sky-300 text-sky-700 hover:bg-sky-50 transition-colors"
                                  title="Course finished — raise Class Over (admin is notified)"
                                >
                                  🎓 Class Over
                                </button>
                              )}
                              <button
                                onClick={() => handleDeactivate(s)}
                                className="px-2.5 py-1 rounded-lg text-xs font-medium border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                              >
                                Deactivate
                              </button>
                            </div>
                          )}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add Student Modal */}
      {modal === 'add' && (
        <Modal title="Add Student" onClose={() => setModal(null)}>
          <form onSubmit={handleAdd} className="space-y-4">
            <ModalField label="Full Name *">
              <input value={newName} onChange={e => setNewName(e.target.value)} required placeholder="Student full name" className="modal-input" />
            </ModalField>
            <ModalField label="Course / Timing *">
              <input value={newCourse} onChange={e => setNewCourse(e.target.value)} required placeholder="e.g. Python â€” 10:00 AM" className="modal-input" />
            </ModalField>
            <ModalField label="Session Type *">
              <select value={newSessionType} onChange={e => {
                const t = e.target.value as 'group' | 'one-to-one';
                setNewSessionType(t);
                setNewCourseFee(t === 'one-to-one' ? '15000' : '20000');
              }} className="modal-input">
                <option value="group">Group (offline) — ₹20,000</option>
                <option value="one-to-one">One-to-One (online) — ₹15,000</option>
              </select>
            </ModalField>
            <ModalField label="Total Course Fee (₹) *">
              <input type="number" value={newCourseFee} onChange={e => setNewCourseFee(e.target.value)}
                min="0" placeholder="20000" className="modal-input" />
              <p className="text-xs text-gray-400 mt-1">Auto-filled by session type. Edit if fee is different for this student.</p>
            </ModalField>
            <ModalField label="Registration Paid (₹)">
              <input type="number" value={newRegPaid} onChange={e => setNewRegPaid(e.target.value)}
                placeholder="1000" className="modal-input" />
              <p className="text-xs text-gray-400 mt-1">Default ₹1000. Balance updated later in Payment Tracker.</p>
            </ModalField>
            <ModalField label="Salesperson (who referred this student)">
              <select
                value={newSalesPerson}
                onChange={e => setNewSalesPerson(e.target.value)}
                className="modal-input">
                <option value="">â€” None â€”</option>
                {salespeople.map(sp => (
                  <option key={sp.name} value={sp.name}>{sp.name}</option>
                ))}
              </select>
            </ModalField>
            <ModalField label="Assign Mentor *">
              <select value={newMentorId} onChange={e => setNewMentorId(e.target.value)} required className="modal-input">
                <option value="">â€” select mentor â€”</option>
                {mentors.map(m => (
                  <option key={m.mentorId} value={m.mentorId}>{m.mentorName}</option>
                ))}
              </select>
            </ModalField>
            <ModalField label="Student / Parent Email (optional)">
              <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="student@email.com" className="modal-input" />
              <p className="text-xs text-gray-400 mt-1">Used to send absent & leave notifications.</p>
              <label className="flex items-center gap-2.5 mt-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={sendWelcome}
                  onChange={e => setSendWelcome(e.target.checked)}
                  disabled={!newEmail}
                  className="w-4 h-4 rounded accent-yellow-400 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                />
                <span className={`text-sm font-medium ${newEmail ? 'text-gray-700' : 'text-gray-400'}`}>
                  Send welcome email to student
                </span>
              </label>
            </ModalField>
            <ModalField label="Notes (optional)">
              <input value={newNotes} onChange={e => setNewNotes(e.target.value)} placeholder="Any notes" className="modal-input" />
            </ModalField>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setModal(null)} className="flex-1 py-2.5 rounded-lg border-2 border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-lg text-sm font-bold text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg,#312741,#5a4a6f)' }}>
                {submitting ? 'Adding…' : 'Add Student'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Reassign Modal */}
      {modal === 'reassign' && selected && (
        <Modal title={`Reassign ${selected.name}`} onClose={() => { setModal(null); setSelected(null); }}>
          <form onSubmit={handleReassign} className="space-y-4">
            <p className="text-sm text-gray-600">
              Current mentor: <strong>{mentorName(selected.mentorId)}</strong>
            </p>
            <ModalField label="New Mentor *">
              <select value={reassignMentorId} onChange={e => setReassignMentorId(e.target.value)} required className="modal-input">
                <option value="">â€” select mentor â€”</option>
                {mentors.map(m => (
                  <option key={m.mentorId} value={m.mentorId}>{m.mentorName}</option>
                ))}
              </select>
            </ModalField>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="flex-1 py-2.5 rounded-lg border-2 border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-lg text-sm font-bold text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg,#312741,#5a4a6f)' }}>
                {submitting ? 'Saving…' : 'Reassign'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Email Modal */}
      {modal === 'email' && selected && (
        <Modal title={`Set Email â€” ${selected.name}`} onClose={() => { setModal(null); setSelected(null); }}>
          <form onSubmit={handleEmailSave} className="space-y-4">
            <p className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
              This email is used to send <strong>absent notifications</strong> and <strong>leave alerts</strong> automatically.
            </p>
            <ModalField label="Student / Parent Email">
              <input
                type="email"
                value={editEmail}
                onChange={e => setEditEmail(e.target.value)}
                placeholder="student@email.com"
                className="modal-input"
                autoFocus
              />
            </ModalField>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }}
                className="flex-1 py-2.5 rounded-lg border-2 border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={submitting}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg,#312741,#5a4a6f)' }}>
                {submitting ? 'Saving…' : 'Save Email'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Phone Modal */}
      {modal === 'phone' && selected && (
        <Modal title={`Set Phone — ${selected.name}`} onClose={() => { setModal(null); setSelected(null); }}>
          <form onSubmit={handlePhoneSave} className="space-y-4">
            <p className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
              This phone number is used for student login (first-time) and contact records.
            </p>
            <ModalField label="Phone Number">
              <input
                type="tel"
                value={editPhone}
                onChange={e => setEditPhone(e.target.value)}
                placeholder="e.g. 9876543210"
                className="modal-input"
                autoFocus
              />
            </ModalField>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }}
                className="flex-1 py-2.5 rounded-lg border-2 border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={submitting}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg,#0f766e,#0d9488)' }}>
                {submitting ? 'Saving…' : 'Save Phone'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Deactivate Reason Modal */}
      {modal === 'deactivate' && selected && (
        <Modal title={`Deactivate ${selected.name}`} onClose={() => { setModal(null); setSelected(null); }}>
          <p className="text-sm text-gray-600 mb-5">
            Why is this student leaving? This cannot be undone easily.
            {coordinatorId && <> <b>Course Finished</b> raises Class Over — admin is emailed the class report.</>}
          </p>
          <div className="space-y-3">
            <button
              disabled={submitting}
              onClick={() => handleDeactivateConfirm('courseFinished')}
              className="w-full py-3 rounded-xl text-sm font-bold border-2 transition-all disabled:opacity-60"
              style={{ borderColor: '#16a34a', color: '#16a34a', background: '#f0fdf4' }}>
              âœ… Course Finished â€” Student completed the course
            </button>
            <button
              disabled={submitting}
              onClick={() => handleDeactivateConfirm('ranAway')}
              className="w-full py-3 rounded-xl text-sm font-bold border-2 transition-all disabled:opacity-60"
              style={{ borderColor: '#9d174d', color: '#9d174d', background: '#fce7f3' }}>
              ðŸš© Ran Away â€” Paid ₹1000 registration, not continuing
            </button>
            <button
              onClick={() => { setModal(null); setSelected(null); }}
              className="w-full py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50 border border-gray-200">
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* Class Over (Student Coordinators) */}
      {classOverStudent && (
        <ClassOverModal
          studentId={classOverStudent.studentId}
          studentName={classOverStudent.name}
          course={classOverStudent.course}
          raisedBy={coordinatorId}
          onDone={() => { setClassOverStudent(null); load(); }}
          onClose={() => setClassOverStudent(null)}
        />
      )}

      {/* Reactivate Modal */}
      {modal === 'reactivate' && selected && (
        <Modal title={`Reactivate ${selected.name}`} onClose={() => { setModal(null); setSelected(null); }}>
          <form onSubmit={handleReactivate} className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800">
              <strong>{selected.name}</strong> ({selected.studentId}) is currently <strong>Inactive</strong>.
              Select a mentor to assign them to and reactivate their account.
            </div>
            <ModalField label="Assign to Mentor *">
              <select value={reactivateMentorId} onChange={e => setReactivateMentorId(e.target.value)} required className="modal-input">
                <option value="">â€” select mentor â€”</option>
                {mentors.map(m => (
                  <option key={m.mentorId} value={m.mentorId}>{m.mentorName}</option>
                ))}
              </select>
            </ModalField>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }}
                className="flex-1 py-2.5 rounded-lg border-2 border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={submitting || !reactivateMentorId}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg,#16a34a,#15803d)' }}>
                {submitting ? 'Reactivating…' : 'Reactivate Student'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Institute Leave Modal */}
      {modal === 'leave' && (
        <Modal title="Mark Leave / Holiday" onClose={() => setModal(null)}>
          <form onSubmit={handleLeave} className="space-y-4">
            <p className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
              This marks all active students as <strong>Absent</strong> on the selected date and records the reason in the Class Log. Notification emails are sent to students who have an email address on file.
            </p>
            <ModalField label="Date *">
              <input type="date" value={leaveDate} onChange={e => setLeaveDate(e.target.value)} required className="modal-input" />
            </ModalField>
            <ModalField label="Reason *">
              <input
                value={leaveReason}
                onChange={e => setLeaveReason(e.target.value)}
                required
                placeholder="e.g. Institute Holiday, Public Holiday, Special Event"
                className="modal-input"
              />
            </ModalField>
            <ModalField label="Apply to (optional â€” leave blank for all students)">
              <select value={leaveMentorId} onChange={e => setLeaveMentorId(e.target.value)} className="modal-input">
                <option value="">All mentors / all active students</option>
                {mentors.map(m => (
                  <option key={m.mentorId} value={m.mentorId!}>Only {m.mentorName}'s students</option>
                ))}
              </select>
            </ModalField>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setModal(null)} className="flex-1 py-2.5 rounded-lg border-2 border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={submitting}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg,#dc2626,#b91c1c)' }}>
                {submitting ? 'Marking…' : 'Mark Leave & Notify'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      <style>{`
        .modal-input { width:100%; padding:0.6rem 0.85rem; border:2px solid #e5e7eb; border-radius:0.5rem; font-size:0.875rem; outline:none; transition:border-color .15s; }
        .modal-input:focus { border-color:#ffe500; }
      `}</style>
    </TrackerLayout>
  );
};

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th className="px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-white/80">{children}</th>
);
const Td: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <td className="px-4 py-3">{children}</td>
);

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 z-10">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-bold" style={{ color: '#312741' }}>{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
      </div>
      {children}
    </div>
  </div>
);

const ModalField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">{label}</label>
    {children}
  </div>
);

export default ManageStudents;

